// Reads the barcode on the back of a US or Canadian driver license or ID card (PDF417, AAMVA
// standard). A USB scanner types the barcode like a keyboard; the camera reads the same text.
//
// The text starts with a header ("@", line feed, record separator, carriage return, "ANSI ",
// the issuer number and the AAMVA version), then the "DL" (driver license) or "ID" part, made of
// three-letter fields, one per line: DCS last name, DAC first name, DAD middle name, DAQ license
// number, DBB date of birth, DBA expiration, DAG street, DAI city, DAJ state, DAK ZIP, and so on.
// Older cards (2000-2005) use DAA for the whole name and DCT for the given names.

export interface LicenseData {
  /** DL: driver license. ID: state ID card. */
  kind: 'DL' | 'ID';
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  /** "John Public": what goes in the customer's name. */
  name: string;
  /** "John Quincy Public": the full name as printed on the license. */
  fullName: string;
  /** License or ID number (DAQ). */
  number: string;
  /** Two-letter state or province that issued it. */
  state: string;
  /** YYYY-MM-DD */
  dob: string | null;
  expires: string | null;
  issued: string | null;
  address: string;
  city: string;
  addressState: string;
  zip: string;
  sex: 'M' | 'F' | null;
  /** USA or CAN */
  country: string | null;
  /** AAMVA version from the header (1-10), when it's there. */
  version: number | null;
}

/** Every field code the AAMVA standard has used (2000-2020), for splitting text that lost its line breaks. */
const CODES = [
  'DAA', 'DAB', 'DAC', 'DAD', 'DAE', 'DAF', 'DAG', 'DAH', 'DAI', 'DAJ', 'DAK', 'DAL', 'DAM', 'DAN', 'DAO', 'DAP', 'DAQ', 'DAR', 'DAS', 'DAT', 'DAU', 'DAV', 'DAW', 'DAX', 'DAY', 'DAZ',
  'DBA', 'DBB', 'DBC', 'DBD', 'DBE', 'DBF', 'DBG', 'DBH', 'DBI', 'DBJ', 'DBK', 'DBL', 'DBM', 'DBN', 'DBO', 'DBP', 'DBQ', 'DBR', 'DBS',
  'DCA', 'DCB', 'DCD', 'DCE', 'DCF', 'DCG', 'DCH', 'DCI', 'DCJ', 'DCK', 'DCL', 'DCM', 'DCN', 'DCO', 'DCP', 'DCQ', 'DCR', 'DCS', 'DCT', 'DCU',
  'DDA', 'DDB', 'DDC', 'DDD', 'DDE', 'DDF', 'DDG', 'DDH', 'DDI', 'DDJ', 'DDK', 'DDL',
];
const CODE_SET = new Set(CODES);
const SPLIT = new RegExp(`(${CODES.join('|')})`, 'g');

/** Placeholder values some states put in empty fields. */
const EMPTY = /^(NONE|UNAVL|UNAVAIL|UNAVAILABLE|UNKNOWN|N\/A|NA|-+|\*+)$/i;

/** True when the text looks like a license barcode (the "ANSI " or "AAMVA" header, or a few license fields). */
export function looksLikeLicense(text: string): boolean {
  if (/ANSI ?\d{6}|AAMVA\d{6}/.test(text)) return true;
  const hits = ['DAQ', 'DCS', 'DAC', 'DBB', 'DAA'].filter((c) => new RegExp(`(^|[\\n\\r\\x1e]|DL|ID)${c}`).test(text));
  return hits.length >= 2;
}

function fields(raw: string): { map: Record<string, string>; kind: 'DL' | 'ID'; version: number | null } {
  const text = raw.replace(/\r\n?/g, '\n').replace(/[\x1e\x1d\x1c\x0b]/g, '\n');
  const head = /(?:ANSI ?|AAMVA)(\d{6})(\d{2})/.exec(text);
  const version = head ? Number(head[2]) : null;
  // The header lists the parts: "DL00410278" (driver license) or "ID00410278" (ID card).
  const after = head ? text.slice(head.index + head[0].length) : text;
  const part = /(DL|ID)\d{8}/.exec(after.slice(0, 60));
  let kind: 'DL' | 'ID' = part?.[1] === 'ID' ? 'ID' : 'DL';
  // The license's part starts with "DL" or "ID" right before its first field.
  const start = /(?:^|[^A-Z])(DL|ID)(?=D[A-D][A-Z])/.exec(after) ?? /(DL|ID)(?=D[A-D][A-Z])/.exec(after);
  let body = after;
  if (start) {
    if (!part) kind = start[1] === 'ID' ? 'ID' : 'DL';
    body = after.slice(start.index + start[0].length);
  }
  const map: Record<string, string> = {};
  const put = (code: string, value: string) => {
    const v = value.replace(/\s+/g, ' ').trim();
    if (!(code in map)) map[code] = v;
  };
  const lines = body.split('\n').map((l) => l.trim()).filter(Boolean);
  const coded = lines.filter((l) => CODE_SET.has(l.slice(0, 3)));
  if (coded.length >= 3) {
    // One field per line (the normal case).
    for (const l of lines) {
      const code = l.slice(0, 3);
      if (CODE_SET.has(code)) put(code, l.slice(3));
    }
  } else {
    // The line breaks were lost on the way (some scanners drop them): split on the field codes.
    const parts = body.replace(/\n/g, '').split(SPLIT);
    for (let i = 1; i < parts.length - 1; i += 2) put(parts[i], parts[i + 1]);
  }
  return { map, kind, version };
}

function val(map: Record<string, string>, ...codes: string[]): string {
  for (const c of codes) {
    const v = map[c];
    if (v && !EMPTY.test(v)) return v;
  }
  return '';
}

function validDate(y: number, m: number, d: number): string | null {
  if (y < 1900 || y > 2200 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** US cards write dates MMDDCCYY, Canadian cards (and the 2000 standard) CCYYMMDD. */
export function licenseDate(value: string, yearFirst = false): string | null {
  const d = value.replace(/\D/g, '');
  if (d.length !== 8) return null;
  const us = () => validDate(Number(d.slice(4, 8)), Number(d.slice(0, 2)), Number(d.slice(2, 4)));
  const ca = () => validDate(Number(d.slice(0, 4)), Number(d.slice(4, 6)), Number(d.slice(6, 8)));
  return yearFirst ? ca() ?? us() : us() ?? ca();
}

const UPPER_WORDS = new Set(['II', 'III', 'IV', 'VI', 'VII', 'VIII', 'NE', 'NW', 'SE', 'SW', 'PO', 'US', 'USA', 'RR', 'HC']);

function cap(word: string): string {
  return word.toLowerCase().replace(/(^|[-'’])([a-z])/g, (_m, p: string, c: string) => p + c.toUpperCase());
}

/** "JOHN" → "John", "O'NEIL-SMITH" → "O'Neil-Smith", "III" stays. */
export function titleName(s: string): string {
  return s.trim().split(/\s+/).filter(Boolean).map((w) => (UPPER_WORDS.has(w.toUpperCase()) ? w.toUpperCase() : cap(w))).join(' ');
}

/** "2570 24TH STREET APT 5B" → "2570 24th Street Apt 5B". */
export function titleAddress(s: string): string {
  return s.trim().split(/\s+/).filter(Boolean).map((w) => {
    const u = w.toUpperCase();
    if (/^\d+(ST|ND|RD|TH)$/.test(u)) return u.replace(/(ST|ND|RD|TH)$/, (x) => x.toLowerCase());
    if (/\d/.test(u) || UPPER_WORDS.has(u.replace(/[.,]/g, '')) || /^[NSEW]\.?$/.test(u)) return u;
    return cap(w);
  }).join(' ');
}

function postal(value: string, canada: boolean): string {
  const v = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (canada || /^[A-Z]\d[A-Z]\d[A-Z]\d/.test(v)) return v.length >= 6 ? `${v.slice(0, 3)} ${v.slice(3, 6)}` : v;
  return v.replace(/\D/g, '').slice(0, 5);
}

/** Reads a license barcode. Gives back null when it isn't one (or the number or name is missing). */
export function parseLicense(raw: string): LicenseData | null {
  if (!raw || raw.length < 20) return null;
  const { map, kind, version } = fields(raw);
  const country = val(map, 'DCG').toUpperCase() || null;
  const canada = country === 'CAN';

  // Names: DCS/DAC/DAD (2009 on), DCS + DCT (2003-2005), DAA "LAST,FIRST,MIDDLE" (2000).
  let last = val(map, 'DCS', 'DAB');
  let first = val(map, 'DAC');
  let middle = val(map, 'DAD');
  const given = val(map, 'DCT');
  if (!first && given) {
    const g = given.split(/[,$\s]+/).filter(Boolean);
    first = g[0] ?? '';
    if (!middle) middle = g.slice(1).join(' ');
  }
  const whole = val(map, 'DAA');
  if ((!first || !last) && whole) {
    const p = /[,$]/.test(whole) ? whole.split(/[,$]/).map((x) => x.trim()) : null;
    if (p) {
      last = last || (p[0] ?? '');
      first = first || (p[1] ?? '');
      middle = middle || p.slice(2).filter(Boolean).join(' ');
    } else {
      const w = whole.split(/\s+/);
      first = first || (w[0] ?? '');
      last = last || (w.length > 1 ? w[w.length - 1] : '');
      middle = middle || w.slice(1, -1).join(' ');
    }
  }
  const suffix = val(map, 'DCU', 'DAE');
  const number = val(map, 'DAQ').toUpperCase().replace(/\s+/g, '');
  if (!number || !(first || last)) return null;

  const yearFirst = canada || version === 1;
  const street = [val(map, 'DAG', 'DAL'), val(map, 'DAH', 'DAM')].filter(Boolean).map(titleAddress).join(', ');
  const sexRaw = val(map, 'DBC').toUpperCase();
  const f = titleName(first);
  const m = titleName(middle);
  const l = titleName(last);
  const sx = suffix ? titleName(suffix.replace(/\.$/, '')) : '';
  const state = (val(map, 'DAJ', 'DAO') || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
  return {
    kind,
    firstName: f,
    middleName: m,
    lastName: l,
    suffix: sx,
    name: [f, l].filter(Boolean).join(' '),
    fullName: [f, m, l, sx].filter(Boolean).join(' '),
    number,
    state,
    dob: licenseDate(val(map, 'DBB'), yearFirst),
    expires: licenseDate(val(map, 'DBA'), yearFirst),
    issued: licenseDate(val(map, 'DBD'), yearFirst),
    address: street,
    city: titleAddress(val(map, 'DAI', 'DAN')),
    addressState: state,
    zip: postal(val(map, 'DAK', 'DAP'), canada),
    sex: sexRaw === '1' || sexRaw === 'M' ? 'M' : sexRaw === '2' || sexRaw === 'F' ? 'F' : null,
    country,
    version,
  };
}

/** Whole years between the date of birth and `today` (both YYYY-MM-DD). */
export function ageOn(dob: string, today: string): number {
  const [by, bm, bd] = dob.split('-').map(Number);
  const [ty, tm, td] = today.split('-').map(Number);
  let a = ty - by;
  if (tm < bm || (tm === bm && td < bd)) a--;
  return a;
}

/** A license is good through the end of its expiration day. */
export function isExpired(expires: string, today: string): boolean {
  return expires < today;
}
